import React, { ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Pencil, Trash2 } from 'lucide-react';

import { Button } from '@ui/components/ui/button';
import { Checkbox } from '@ui/components/ui/checkbox';
import { Input } from '@ui/components/ui/input';
import { MultiSelectField } from '@ui/components/ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  selectItemsByValue,
} from '@ui/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/ui/tooltip';

import { cardClass, scrollRowClass } from '@app/constants/styles';
import { EditSheet } from '@app/components/core/edit-sheet';
import { useIsMobile } from '@ui/hooks/use-mobile';
import { Roles } from '@app/utils/types';
import { MembershipStatus, ProductEntity, ProductTeamMember } from '@data';
import { useListPmMembers } from '@data/hooks/membership';
import { useOrganizationChapters } from '@data/hooks/organization';
import { useStore } from '@data/storages';
import { useUpdateProductDetail } from '@data/hooks/products';

import { SENIORITY_LEVELS } from '../../members/memberMock';
import {
  chapterStoreKey,
  effectiveChapter,
  effectiveSeniority,
  loadProductChapters,
  loadProductSeniority,
  seniorityKey,
} from '../productMock';
import { productStore } from './productStore';
import CostMatrix from './CostMatrix';
import { formatEur } from './budget';
import { readPricing, writePricing, type VersionPricing } from './pricing';
import VersionPricingCard from './VersionPricingCard';
import ServiceHours from './ServiceHours';
import VersionPicker from './VersionPicker';
import { type ProductVersion, versionFor, yearOf } from './productVersions';
import { useProductVersions } from './useProductVersions';
import { useReportUnsaved } from './unsavedWork';
import { useActiveMembers } from './activeMembers';
import { ACTIVE_HOURS } from './activeTeam';
import { onVersionTeam, readAddedTo, reportedIn, writeAddedTo } from './versionTeam';

const viewLabel = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';
const pillClass = 'shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground';
const dash = <span className="text-muted-foreground">—</span>;

type TeamGroup = 'active' | 'others';

/** A team row while it is being edited. The share is held as typed, so a half-typed number is not a 0. */
type TeamDraftRow = {
  membershipId: string;
  name: string;
  isManager: boolean;
  rndShare: string;
  /** Reported time put them here, so the hours have to go before the row can. */
  joinedByTime: boolean;
  /** Has reported time here, which keeps them on the team whatever else is edited. */
  hasReported: boolean;
  /** Reported inside this version's dates, which keeps them on its team. */
  reportedHere: boolean;
  /** Put on this version by hand. */
  addedHere: boolean;
  /** On this version's team. The rest stay in the draft so a save keeps them. */
  inSpan: boolean;
};

const nameOf = (person: { firstName?: string; lastName?: string }): string =>
  `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim();

const toDraft = (
  team: ProductTeamMember[],
  version: Pick<ProductVersion, 'startDate' | 'endDate'> | null,
  added: string[],
): TeamDraftRow[] =>
  team.map(member => ({
    membershipId: member.membershipId,
    name: nameOf(member),
    isManager: member.isManager,
    rndShare: String(member.rndShare),
    joinedByTime: !!member.joinedByTime,
    hasReported: !!member.hasReported,
    reportedHere: !!version && reportedIn(member, version),
    addedHere: added.includes(member.membershipId),
    inSpan: onVersionTeam(member, version, added),
  }));

const addedIds = (rows: TeamDraftRow[]): string[] =>
  rows
    .filter(row => row.addedHere)
    .map(row => row.membershipId)
    .sort();

// Emptied used to save as 100, and a fraction is refused here because @IsInt() would refuse the whole save unnamed.
const shareErrorKey = (rows: TeamDraftRow[]): string | null => {
  const shares = rows.map(row => ({ text: row.rndShare, value: Number(row.rndShare) }));
  if (shares.some(({ text, value }) => text.trim() === '' || !(value >= 0) || value > 100)) {
    return 'productDetail.shareOutOfRange';
  }
  if (shares.some(({ value }) => !Number.isInteger(value))) return 'productDetail.shareNotWhole';

  return null;
};

const sameTeam = (a: TeamDraftRow[], b: TeamDraftRow[]): boolean =>
  JSON.stringify(a.map(row => [row.membershipId, row.isManager, row.rndShare]).sort()) ===
  JSON.stringify(b.map(row => [row.membershipId, row.isManager, row.rndShare]).sort());

const sameLevels = (a: Record<string, string>, b: Record<string, string>): boolean =>
  JSON.stringify(Object.entries(a).sort()) === JSON.stringify(Object.entries(b).sort());

/** A tap is handled here: the library closes on pointerdown and guards the focus that follows. */
const HeadingHint = ({ label, children }: { label: string; children: ReactNode }): ReactElement => {
  const [open, setOpen] = useState(false);

  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={() => setOpen(current => !current)}
          aria-label={label}
          className="inline-flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          <Info className="size-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent>{children}</TooltipContent>
    </Tooltip>
  );
};

/** What this product is sold on, so Budget and Spend can be worked out from it later. */
const TeamAndRatesTab = ({
  product,
  organizationId,
  canEdit,
  openOn,
  pricing,
}: {
  product: ProductEntity;
  organizationId: string;
  canEdit: boolean;
  /** A day to open on the version covering, as when a sprint is priced, instead of today's. */
  openOn?: string;
  /** Pricing one sprint from the send step: always editing, only what reaches the mail, gaps marked. */
  pricing?: {
    /** Who has no seniority: their level is marked until one is picked. */
    highlight: string[];
    chapters: string[];
    missingRates: { chapterKey: string; level: string }[];
    /** Only these rows are shown; the rest of the team is kept as it is and saved untouched. */
    workers: string[];
  };
}): ReactElement => {
  const { t } = useTranslation();
  // R&D shares feed the tax exemption, which only an admin handles.
  const showRnd = useStore().organization?.role === Roles.admin;
  const save = useUpdateProductDetail(product.id);
  const versions = useProductVersions(product.id);
  const openedOn = useRef(false);
  const openVersion = openOn ? versionFor(versions.versions, openOn)?.number : undefined;
  useEffect(() => {
    if (openedOn.current || !openVersion) return;
    openedOn.current = true;
    versions.select(openVersion);
  }, [openVersion, versions]);
  const versionNumber = versions.version?.number;
  const from = versions.version?.startDate;
  const to = versions.version?.endDate;
  const span = useMemo(() => (from && to ? { from, to } : undefined), [from, to]);
  const teamRows = useMemo(() => product.team ?? [], [product.team]);
  const activeMembers = useActiveMembers(product.id, span);

  const [editing, setEditing] = useState(false);
  // Which of the two team cards the open edit belongs to; null edits the whole team in one card.
  const [editGroup, setEditGroup] = useState<TeamGroup | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [team, setTeam] = useState<TeamDraftRow[]>([]);
  const [teamBaseline, setTeamBaseline] = useState<TeamDraftRow[]>([]);
  // This version's answer for a person's level. No entry means their profile's, until the product saves its own.
  const [levels, setLevels] = useState<Record<string, string>>({});
  const [levelsBaseline, setLevelsBaseline] = useState<Record<string, string>>({});
  const [chapterPicks, setChapterPicks] = useState<Record<string, string>>({});
  const [chapterPicksBaseline, setChapterPicksBaseline] = useState<Record<string, string>>({});

  const [versionPricing, setVersionPricing] = useState<VersionPricing>({ mode: 'timeAndMaterials' });
  useEffect(() => {
    setVersionPricing(readPricing(product.id, versionNumber));
  }, [product.id, versionNumber, versions.revision]);
  const atCost = versionPricing.mode === 'atCost';
  const rateCardUnused = versionPricing.mode !== 'timeAndMaterials';
  const choosePricing = (next: VersionPricing): void => {
    writePricing(product.id, versionNumber, next);
    setVersionPricing(next);
  };
  const hourlyCostOf = (membershipId: string): number | undefined =>
    teamRows.find(row => row.membershipId === membershipId)?.hourlyCost;

  // Only who can be added comes from here, so a client, who adds nobody, never asks for our member list.
  const { data: membersData } = useListPmMembers(organizationId, {}, canEdit);
  const members = useMemo(() => membersData?.items ?? [], [membersData]);
  const { data: chapterData } = useOrganizationChapters(organizationId, { includeDisabled: true });
  const chapters = useMemo(() => chapterData?.items ?? [], [chapterData]);

  // Mirrors the server while not editing; the sync after a save is skipped so it cannot flash the old rows back.
  const skipNextSync = useRef(false);
  // The version the open edit belongs to, or switching mid-edit saves the levels onto the wrong one.
  const editingVersion = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (editing) return;
    if (skipNextSync.current) {
      skipNextSync.current = false;
      return;
    }
    // Read here rather than held in state, so a save cannot re-run this against the team from before it.
    const added = readAddedTo(product.id, versionNumber);
    const draft = toDraft(teamRows, span ? { startDate: span.from, endDate: span.to } : null, added);
    setTeam(draft);
    setTeamBaseline(draft);
  }, [teamRows, span, product.id, versionNumber, versions.revision, editing]);

  // Re-read on the version as well as the product: a level is that version's answer, not the product's.
  useEffect(() => {
    if (editing) return;
    const stored = loadProductSeniority(product.id, versionNumber);
    setLevels(stored);
    setLevelsBaseline(stored);
    const storedChapters = loadProductChapters(product.id, versionNumber);
    setChapterPicks(storedChapters);
    setChapterPicksBaseline(storedChapters);
  }, [product.id, versionNumber, versions.revision, editing]);

  // Clients are never on a product's team: they are the people it is reported to, not the people on it.
  const addOptions = useMemo(
    () =>
      members
        .filter(membership => membership.user?.status !== MembershipStatus.deactivated)
        .filter(membership => membership.role !== Roles.client)
        // Somebody on the product but not on this version can be added to it.
        .filter(membership => !team.some(row => row.membershipId === membership.id && row.inSpan))
        .map(membership => ({ value: membership.id, label: nameOf(membership.user) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [members, team],
  );

  // Their profile's chapter: their default, or the first they reported under here.
  const joinedChapterOf = (membershipId: string): string => {
    const onTeam = teamRows.find(row => row.membershipId === membershipId);
    if (onTeam) return onTeam.chapterKey ?? '';
    // Somebody added in this edit is not on the product's team yet, so the member list answers for them.
    const membership = members.find(candidate => candidate.id === membershipId);
    if (membership?.defaultChapter?.isActive) return membership.defaultChapter.key;
    return membership?.chapters?.[0] ?? '';
  };
  const chapterOf = (membershipId: string): string =>
    effectiveChapter(chapterPicks[membershipId], joinedChapterOf(membershipId));
  const chapterText = (key: string): string => chapters.find(chapter => chapter.key === key)?.label ?? key;
  const chapterOptions = (current: string) =>
    chapters
      .filter(chapter => chapter.isActive || chapter.key === current)
      .map(chapter => ({ value: chapter.key, label: chapter.label }));

  // Their profile's level: what they join on, and what shows until the product keeps its own.
  const joinedLevelOf = (membershipId: string): string =>
    (
      teamRows.find(row => row.membershipId === membershipId) ??
      members.find(candidate => candidate.id === membershipId)
    )?.seniority ?? '';
  const levelOf = (membershipId: string): string =>
    effectiveSeniority(levels[membershipId], joinedLevelOf(membershipId));
  const levelText = (level: string): ReactNode =>
    level ? t(`seniorityLevels.${level}`, { defaultValue: level }) : dash;

  const levelOptions = SENIORITY_LEVELS.map(level => ({ value: level, label: t(`seniorityLevels.${level}`) }));

  // Pricing is here to be edited, so it opens editing, and again after a save, once the version it prices is on screen.
  const pricingReady = !!pricing && !!versionNumber && (!openVersion || versionNumber === openVersion);
  useEffect(() => {
    if (!pricingReady || editing) return;
    editingVersion.current = versionNumber;
    setEditing(true);
  }, [pricingReady, editing, versionNumber]);

  const teamDirty = !sameTeam(team, teamBaseline);
  const addedDirty = addedIds(team).join() !== addedIds(teamBaseline).join();
  const levelsDirty = !sameLevels(levels, levelsBaseline) || !sameLevels(chapterPicks, chapterPicksBaseline);
  // Every row's shown level, empty ones too, so a later profile change leaves this product alone.
  const shownLevels = Object.fromEntries(team.map(row => [row.membershipId, levelOf(row.membershipId)]));
  // A level only shown off the profile can be saved as it is, which is how a product's existing team gets its own.
  const shownChapters = Object.fromEntries(
    team.map(row => [row.membershipId, chapterOf(row.membershipId)]).filter(([, chapter]) => chapter),
  );
  const unstoredLevels = !sameLevels(shownLevels, levelsBaseline) || !sameLevels(shownChapters, chapterPicksBaseline);
  const shareError = shareErrorKey(team);
  const canSave = (teamDirty || addedDirty || levelsDirty || unstoredLevels) && !shareError && !save.isPending;
  // Switching version mid-edit saves under the version being left, so the picker has to ask first.
  useReportUnsaved('teamAndRates', editing && (teamDirty || addedDirty || levelsDirty));

  const stopEditing = (): void => {
    setTeam(teamBaseline);
    setLevels(levelsBaseline);
    setChapterPicks(chapterPicksBaseline);
    setFailed(null);
    setEditing(false);
  };

  // An edit held open across a version change would save under the version it began on, so it ends here.
  useEffect(() => {
    if (!editing || editingVersion.current === versionNumber) return;
    stopEditing();
  }, [versionNumber, editing]);

  const edited = (row: TeamDraftRow): boolean => {
    const before = teamBaseline.find(other => other.membershipId === row.membershipId);
    return !before || before.isManager !== row.isManager || before.rndShare !== row.rndShare;
  };

  const onSave = (): void => {
    setFailed(null);

    const writeLevels = (): void => {
      if (!unstoredLevels) return;
      const version = editingVersion.current ?? versionNumber;
      productStore.setItem(seniorityKey(product.id, version), JSON.stringify(shownLevels));
      productStore.setItem(chapterStoreKey(product.id, version), JSON.stringify(shownChapters));
      setLevels(shownLevels);
      setLevelsBaseline(shownLevels);
      setChapterPicks(shownChapters);
      setChapterPicksBaseline(shownChapters);
    };

    const writeAdded = (): void => {
      if (!addedDirty) return;
      writeAddedTo(product.id, editingVersion.current ?? versionNumber, addedIds(team));
    };

    if (!teamDirty) {
      writeLevels();
      writeAdded();
      setEditing(false);
      return;
    }

    save.mutate(
      {
        // Sent whole: a row missing here comes off the team. A row reports put here is sent once edited, which adds it.
        team: team
          .filter(row => !row.joinedByTime || edited(row))
          .map(row => ({
            membershipId: row.membershipId,
            rndShare: Number(row.rndShare),
            isManager: row.isManager,
          })),
      },
      {
        // Written only once the team is accepted, or a refusal leaves them committed and Cancel cannot undo it.
        onSuccess: () => {
          skipNextSync.current = true;
          writeLevels();
          writeAdded();
          setEditing(false);
        },
        // Kept open with what was typed still in it, because a save that failed has nothing to show yet.
        onError: error => {
          const status = (error as { response?: { status?: number } })?.response?.status;
          setFailed(status === 400 ? t('productDetail.saveRejected') : t('productDetail.saveFailed'));
        },
      },
    );
  };

  const isMobile = useIsMobile();
  // A phone edits the team in a sheet (below), except when pricing: that already sits in the send dialog.
  const inSheet = isMobile && !pricing;
  const inline = editing && !inSheet;

  const cardActions = (group: TeamGroup | null, hasRows: boolean): ReactNode => {
    // Nobody can be added to the active card, so with nobody in it there is nothing to edit.
    if (!canEdit || (group === 'active' && !hasRows)) return null;
    if (!inline) {
      return (
        <Button
          variant="outline"
          size="sm"
          className="shrink-0 bg-card shadow-xs"
          onClick={() => {
            editingVersion.current = versionNumber;
            setEditGroup(group);
            setEditing(true);
          }}
        >
          <Pencil className="size-4" />
          {t('edit')}
        </Button>
      );
    }
    // One card is edited at a time: the other waits, since both save the same team.
    if (editGroup !== group) return null;
    return (
      <div className="flex shrink-0 gap-2">
        <Button variant="ghost" size="sm" disabled={save.isPending} onClick={stopEditing}>
          {t('actions.cancel')}
        </Button>
        <Button size="sm" disabled={!canSave} onClick={onSave}>
          {t('actions.save')}
        </Button>
      </div>
    );
  };

  const addPersonField = (
    <MultiSelectField
      single
      label={<span className="sr-only">{t('addPerson')}</span>}
      options={addOptions}
      value={[]}
      onChange={vals => {
        const membershipId = vals[0];
        const option = addOptions.find(candidate => candidate.value === membershipId);
        if (!option) return;
        // Already on the product, so only this version gains them.
        if (team.some(row => row.membershipId === membershipId)) {
          setTeam(prev =>
            prev.map(row => (row.membershipId === membershipId ? { ...row, addedHere: true, inSpan: true } : row)),
          );
          return;
        }
        // Everybody starts wholly R&D, which is the common case; the share is edited from there.
        setTeam(prev => [
          ...prev,
          {
            membershipId,
            name: option.label,
            isManager: false,
            rndShare: '100',
            joinedByTime: false,
            hasReported: false,
            reportedHere: false,
            addedHere: true,
            inSpan: true,
          },
        ]);
      }}
      emptyLabel={t('addPerson')}
      searchPlaceholder={t('search')}
      emptySearchLabel={t('noMatches')}
      className="max-w-xs"
    />
  );

  const teamTable = (rows: TeamDraftRow[]): ReactElement => (
    // Its own sideways scroll on a phone, with the columns at a width their names can be read at.
    <div className={scrollRowClass}>
      <div className="max-md:min-w-[34rem]">
        <div className={`flex items-center gap-3 border-b border-border pb-2 ${viewLabel}`}>
          <span className="min-w-0 flex-1">{t('member')}</span>
          <span className="min-w-0 flex-1">{t('chapter')}</span>
          <span className="w-24 shrink-0">{t('seniority')}</span>
          {atCost && <span className="w-24 shrink-0 text-right">{t('pricing.hourlyCost')}</span>}
          {showRnd && (
            <span className="ml-8 flex w-24 shrink-0 items-center justify-end gap-1.5 text-right">
              {t('rnd')}
              <HeadingHint label={t('rndInfoLabel')}>{t('rndInfo')}</HeadingHint>
            </span>
          )}
        </div>
        <div className="divide-y divide-border">
          {rows.map(row => (
            <div key={row.membershipId} className="flex items-center gap-3 py-2.5">
              <span className="flex min-w-0 flex-1 items-center gap-2">
                <span className="truncate text-sm font-medium text-foreground">{row.name}</span>
                {row.isManager ? <span className={pillClass}>{t('manager')}</span> : null}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground">
                {chapterOf(row.membershipId) ? chapterText(chapterOf(row.membershipId)) : dash}
              </span>
              <span className="w-24 shrink-0 truncate text-sm text-muted-foreground">
                {levelText(levelOf(row.membershipId))}
              </span>
              {atCost && (
                <span className="w-24 shrink-0 text-right text-sm tabular-nums text-foreground">
                  {hourlyCostOf(row.membershipId) === undefined
                    ? dash
                    : formatEur(hourlyCostOf(row.membershipId) as number)}
                </span>
              )}
              {showRnd && (
                <span className="ml-16 w-16 shrink-0 text-right text-sm text-muted-foreground">{row.rndShare}%</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Split like the dashboard's Active team card; everyone in one table until the reports answer.
  const shownTeam = team.filter(row => row.inSpan);
  const activeRows = shownTeam.filter(row => activeMembers.active.has(row.membershipId));
  const otherRows = shownTeam.filter(row => !activeMembers.active.has(row.membershipId));
  // Two cards once the split is known, and one when the hours failed rather than a false "nobody active".
  const splitKnown = !activeMembers.isLoading && !activeMembers.isError;
  // An open edit keeps the layout it began in, whatever loads meanwhile.
  const isSplit = !pricing && !!shownTeam.length && (editing ? editGroup !== null : splitKnown);

  const teamEdit = (editRows: TeamDraftRow[], canAdd: boolean): ReactElement => (
    <div className="space-y-4">
      {editRows.length ? (
        <div className="overflow-x-auto rounded-lg border border-border">
          <div className={pricing ? '' : 'min-w-[52rem]'}>
            <div className={`flex items-center gap-4 border-b border-border px-3 py-2 ${viewLabel}`}>
              <span className="min-w-0 flex-1">{t('member')}</span>
              {!pricing && <span className="w-16 shrink-0 text-center">{t('manager')}</span>}
              <span className="min-w-0 flex-1">{t('chapter')}</span>
              <span className="w-40 shrink-0">{t('seniority')}</span>
              {!pricing && showRnd && (
                <span className="flex w-24 shrink-0 items-center gap-1.5">
                  {t('rnd')}
                  <HeadingHint label={t('rndInfoLabel')}>{t('rndInfo')}</HeadingHint>
                </span>
              )}
              {!pricing && <span className="w-8 shrink-0" />}
            </div>
            <div className="divide-y divide-border">
              {editRows.map(row => (
                <div key={row.membershipId} className="flex items-center gap-4 px-3 py-2.5">
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{row.name}</span>
                  {!pricing && (
                    <span className="flex w-16 shrink-0 justify-center">
                      <Checkbox
                        aria-label={`${row.name}, ${t('manager')}`}
                        checked={row.isManager}
                        onCheckedChange={checked =>
                          setTeam(prev =>
                            prev.map(other =>
                              other.membershipId === row.membershipId
                                ? { ...other, isManager: checked === true }
                                : other,
                            ),
                          )
                        }
                      />
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <Select
                      value={chapterOf(row.membershipId) || null}
                      onValueChange={chapter => {
                        if (chapter) setChapterPicks(prev => ({ ...prev, [row.membershipId]: chapter }));
                      }}
                      items={selectItemsByValue(chapterOptions(chapterOf(row.membershipId)))}
                    >
                      <SelectTrigger
                        size="sm"
                        className="w-full bg-popover"
                        aria-label={`${row.name}, ${t('chapter')}`}
                      >
                        <SelectValue placeholder={t('budget.noChapter')} />
                      </SelectTrigger>
                      <SelectContent className="min-w-0" align="start" alignItemWithTrigger={false}>
                        {chapterOptions(chapterOf(row.membershipId)).map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* This product's own answer: the same person can be senior here and middle elsewhere. */}
                  <div className="w-40 shrink-0">
                    {/* Normalised like the read-only row: an old-ladder "medior" otherwise prints verbatim. */}
                    <Select
                      value={levelOf(row.membershipId) || null}
                      onValueChange={level => {
                        if (level) setLevels(prev => ({ ...prev, [row.membershipId]: level }));
                      }}
                      items={selectItemsByValue(levelOptions)}
                    >
                      <SelectTrigger
                        size="sm"
                        // Marked until a level is picked, so the tint is always the thing still to fill in.
                        className={
                          pricing?.highlight.includes(row.membershipId) && !levelOf(row.membershipId)
                            ? 'w-full border-amber-500/60 bg-amber-500/10'
                            : 'w-full bg-popover'
                        }
                        aria-label={`${row.name}, ${t('seniority')}`}
                      >
                        <SelectValue placeholder={t('noSeniority')} />
                      </SelectTrigger>
                      <SelectContent className="min-w-0" align="start" alignItemWithTrigger={false}>
                        {levelOptions.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {!pricing && showRnd && (
                    <div className="flex w-24 shrink-0 items-center gap-1.5">
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        aria-label={`${row.name}, ${t('rndShare')}`}
                        value={row.rndShare}
                        onChange={e =>
                          setTeam(prev =>
                            prev.map(other =>
                              other.membershipId === row.membershipId ? { ...other, rndShare: e.target.value } : other,
                            ),
                          )
                        }
                        className="w-16 bg-popover"
                      />
                      <span className="text-sm text-muted-foreground">%</span>
                    </div>
                  )}
                  {/* Hours in this version keep them on it; hours elsewhere keep them on the product, so only this version loses them. */}
                  {!pricing && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      disabled={row.reportedHere}
                      title={row.reportedHere ? t('teamReported') : undefined}
                      aria-label={`${t('actions.remove')} ${row.name}`}
                      onClick={() =>
                        setTeam(prev =>
                          row.hasReported
                            ? prev.map(other =>
                                other.membershipId === row.membershipId
                                  ? { ...other, addedHere: false, inSpan: false }
                                  : other,
                              )
                            : prev.filter(other => other.membershipId !== row.membershipId),
                        )
                      }
                      className="shrink-0 text-muted-foreground hover:text-foreground"
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            {!pricing && canAdd && <div className="border-t border-border p-2">{addPersonField}</div>}
          </div>
        </div>
      ) : (
        canAdd && addPersonField
      )}
      {!pricing && (
        <p className="text-xs text-muted-foreground">
          {t(showRnd ? 'productDetail.teamHint' : 'productDetail.teamHintWithoutRnd')}
        </p>
      )}
      {shareError ? <p className="text-sm text-destructive">{t(shareError)}</p> : null}
      {failed ? <p className="text-sm text-destructive">{failed}</p> : null}
    </div>
  );

  // The sheet edits the rows of the card it was opened from, or the whole team from the single card.
  const sheetRows = (editGroup === 'active' && activeRows) || (editGroup === 'others' && otherRows) || shownTeam;
  const sheetTitle = t({ active: 'teamActive', others: 'teamOthers' }[editGroup ?? ''] ?? 'teamMembers');

  const teamCard = ({
    group,
    title,
    hint,
    rows,
    empty,
  }: {
    group: TeamGroup | null;
    title: string;
    hint?: string;
    rows: TeamDraftRow[];
    empty: string;
  }): ReactElement => {
    const isEditing = inline && editGroup === group;
    const canAdd = group !== 'active';
    let body: ReactNode = <span className="text-sm text-muted-foreground">{empty}</span>;
    if (isEditing) body = teamEdit(rows, canAdd);
    else if (rows.length) body = teamTable(rows);
    return (
      <section className={cardClass}>
        <div className="mb-4 flex items-start justify-between gap-4 print:hidden">
          <div>
            <h2 className="text-base font-semibold text-foreground">{title}</h2>
            {hint && <p className="text-sm text-muted-foreground">{hint}</p>}
          </div>
          {cardActions(group, rows.length > 0)}
        </div>
        {body}
      </section>
    );
  };

  return (
    <div className="space-y-8">
      {/* Which version of the terms the cards below show, beside the first of them rather than the page. */}
      <div className="space-y-2">
        {/* The picker is rendered whether or not there is a version: with none it is what says why. */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          {versions.version ? (
            <h2 className="text-lg font-bold text-foreground">
              {t('teamRatesTitle', { year: yearOf(versions.version.startDate) })}
            </h2>
          ) : null}
          <VersionPicker versions={versions} />
        </div>
        {!pricing && (
          <VersionPricingCard
            productId={product.id}
            version={versions.version ?? undefined}
            pricing={versionPricing}
            canEdit={canEdit}
            onChange={choosePricing}
          />
        )}
        {isSplit ? (
          <>
            {teamCard({
              group: 'active',
              title: t('teamActive'),
              hint: t('teamActiveHint', { hours: ACTIVE_HOURS }),
              rows: activeRows,
              empty: t('teamActiveNone'),
            })}
            {/* Somebody new has no hours yet, so this is the card people are added from. */}
            {teamCard({
              group: 'others',
              title: t('teamOthers'),
              hint: t(showRnd ? 'teamMembersHint' : 'teamMembersHintWithoutRnd'),
              rows: otherRows,
              empty: t('teamOthersNone'),
            })}
          </>
        ) : (
          teamCard({
            group: null,
            title: t('teamMembers'),
            hint: pricing ? undefined : t(showRnd ? 'teamMembersHint' : 'teamMembersHintWithoutRnd'),
            rows: pricing ? team.filter(row => pricing.workers.includes(row.membershipId)) : shownTeam,
            empty: t(team.length ? 'productDetail.noTeamThisVersion' : 'productDetail.noTeam'),
          })
        )}
        <EditSheet
          open={editing && inSheet}
          title={sheetTitle}
          onCancel={stopEditing}
          onSubmit={event => {
            event.preventDefault();
            onSave();
          }}
          isPending={save.isPending}
          canSave={canSave}
        >
          {teamEdit(sheetRows, editGroup !== 'active')}
        </EditSheet>
      </div>

      {/* Greyed rather than hidden: the prices are kept, and come back if the version goes back to the rate card. */}
      <div
        aria-disabled={rateCardUnused}
        className={rateCardUnused ? 'pointer-events-none select-none opacity-50' : undefined}
      >
        <CostMatrix
          canEdit={canEdit && !rateCardUnused}
          version={versionNumber}
          productId={product.id}
          onlyChapters={pricing?.chapters}
          markedCells={pricing?.missingRates}
        />
      </div>
      {!pricing && <ServiceHours canEdit={canEdit} version={versionNumber} />}
    </div>
  );
};

export default TeamAndRatesTab;
